// What you're looking at (docs/ARCHITECTURE.md §2.3): the tab and filters, the open sheet, the list's stop, where the
// map is, and your location. One small store; everything shown derives from it. The map reads it by subscribing
// (MapController), components with useView. Objects inside are replaced, never edited, so a change is a new identity.
import { createStore, useStore } from "zustand";
import { RULES, type Box, type LatLng } from "../shared/rules";

export interface Filter {
  readonly time: string;
  readonly freeOnly: boolean;
  readonly maxPrice: number | null;
  /** The types picked in Types (normalized tags). */
  readonly tags: ReadonlySet<string>;
  /** "All interests": any of your interests, as they change. */
  readonly allInterests: boolean;
  readonly search: string;
}

export type Sheet =
  | { kind: "event"; id: string; full?: boolean }
  | { kind: "results"; title: string; ids: string[] }
  | { kind: "myevents" }
  | { kind: "profile" };

/** Where the map is, set by the map when it settles (React never asks the map). */
export interface Camera { bounds: Box; center: LatLng; zoom: number }

/** A request for the map to move (React → map, the one way). `seq` makes a repeat of the same request new. */
export interface MapMove { center: LatLng; zoom?: number; atLeast?: boolean; seq: number }

export type Popover = "price" | "types" | null;

export interface ViewState {
  filter: Filter;
  sheet: Sheet | null;
  /** Phone width: the list pulled up from its bar. */
  nearbyExpanded: boolean;
  userLocation: LatLng | null;
  findingYou: boolean;
  camera: Camera | null;
  move: MapMove | null;
  popover: Popover;
}

export const FIRST_TAB = "today";

export const viewStore = createStore<ViewState>()(() => ({
  filter: { time: FIRST_TAB, freeOnly: false, maxPrice: null, tags: new Set(), allInterests: false, search: "" },
  sheet: null,
  nearbyExpanded: false,
  userLocation: null,
  findingYou: false,
  camera: null,
  move: null,
  popover: null,
}));

export function useView<T>(pick: (s: ViewState) => T) { return useStore(viewStore, pick); }
const get = () => viewStore.getState();
const setFilter = (change: Partial<Filter>) => viewStore.setState({ filter: { ...get().filter, ...change } });

export const isSearching = (f: Filter = get().filter) => f.search.trim() !== "";

// ---------- filters ----------
export const setTab = (time: string) => { if (get().filter.time !== time) setFilter({ time }); };
/** Every change to Free or the price goes through here (RULES.freePrice: they exclude each other). */
export function setFreePrice(change: { freeOnly: boolean } | { maxPrice: number | null }) {
  setFilter(RULES.freePrice(get().filter, change));
}
export function toggleType(tag: string) {
  const f = get().filter, k = RULES.norm(tag);
  const tags = new Set(f.allInterests ? [] : f.tags);
  if (tags.has(k)) tags.delete(k); else tags.add(k);
  setFilter({ tags, allInterests: false });
}
export const toggleAllInterests = () => setFilter({ allInterests: !get().filter.allInterests, tags: new Set() });
export const resetTypes = () => setFilter({ allInterests: false, tags: new Set() });
export const setSearch = (search: string) => { if (get().filter.search !== search) setFilter({ search }); };

// ---------- the list, sheets, popovers ----------
export const setNearbyExpanded = (nearbyExpanded: boolean) => viewStore.setState({ nearbyExpanded });
export const setPopover = (popover: Popover) => { if (get().popover !== popover) viewStore.setState({ popover }); };
/** Opening a sheet drops the list back to its bar, so closing comes back there (like the phone). */
export const openSheet = (sheet: Sheet) => viewStore.setState({ sheet, nearbyExpanded: false });
/** Closes whatever sheet is open (an event's card included): back to the list. */
export const closeSheet = () => { if (get().sheet) viewStore.setState({ sheet: null }); };
/** The My events / Profile buttons: tapping again while its sheet is open closes it. */
export const toggleSheet = (kind: "myevents" | "profile") => (get().sheet?.kind === kind ? closeSheet() : openSheet({ kind }));
export const useSheet = () => useStore(viewStore, (s) => s.sheet);
export const selectedId = (s: ViewState = get()) => (s.sheet?.kind === "event" ? s.sheet.id : null);

// ---------- the map ----------
let moveSeq = 0;
/** Moves the map: `zoom` exactly, or with `atLeast` only zooming in to it. */
export function moveMap(center: LatLng, zoom?: number, atLeast = false) {
  viewStore.setState({ move: { center, zoom, atLeast, seq: ++moveSeq } });
}
export const setCamera = (camera: Camera) => viewStore.setState({ camera });
export const setUserLocation = (userLocation: LatLng) => viewStore.setState({ userLocation, findingYou: false });
export const setFindingYou = (findingYou: boolean) => viewStore.setState({ findingYou });
